import {
  Button,
  ChatBubbleOvalLeftEllipsisIcon,
  Cog6ToothIcon,
  Dialog,
  Flex,
  Panel,
  PlusIcon,
  Text,
  Tooltip,
} from 'components'
import { ChevronLeftIcon } from 'components/icons/hero-icons'
import { isEmpty } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { forceLight } from 'utils'
import TemplatePreviewButton from '../components/TemplatePreviewButton'
import {
  MAX_DESCRIPTION_LENGTH,
  ManageTemplateLayout,
  ManageTemplateRegistration,
  ManageTemplateTraits,
  useTemplate,
  type ManageTemplateTraitsHandle,
} from '../components/template'
import TemplateManage from '../components/template/TemplateManage'

export default function TemplateInfoPage() {
  const manageTraitsRef = useRef<ManageTemplateTraitsHandle>(null)
  const { template, isDirty, saveTemplate, resetTemplate } = useTemplate()
  const [layoutManageKey, setLayoutManageKey] = useState<number>()
  const [showRegistration, setShowRegistration] = useState(false)
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    if (template && !template.title) setShowSettings(true)
  }, [template])

  if (!template) return null

  const { title, description } = template
  const descriptionLength = description?.en?.length ?? 0
  const isError = !title || descriptionLength > MAX_DESCRIPTION_LENGTH
  const isLayoutManageMode = layoutManageKey != null
  const routingEmpty = isEmpty(template.routing)

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={{ base: 5, md: 0 }}
      pb={{ base: undefined, md: 4 }}
      pr={{ base: undefined, md: 5 }}
      position="relative"
      overflowY="auto"
      overflowX="hidden"
      mt={2}
    >
      <Flex alignItems="stretch" gap={2} width="100%">
        <Flex alignItems="center" flex={1} justifyContent="flex-start" minWidth={0}>
          {isLayoutManageMode && (
            <Tooltip content="Back to trait routing">
              <Button
                leftIcon={<ChevronLeftIcon boxSize={4} type="outline" />}
                onClick={() => setLayoutManageKey(undefined)}
                px={3}
                variant="ghost"
              >
                Back
              </Button>
            </Tooltip>
          )}
        </Flex>
        <Flex alignItems="stretch" flexShrink={0} flexWrap="wrap" gap={2} justifyContent="center">
          {isDirty && (
            <>
              <Button onClick={resetTemplate} variant="ghost">
                Reset
              </Button>
              <Button onClick={saveTemplate} disabled={isError} palette="primary">
                Save
              </Button>
            </>
          )}
          <TemplatePreviewButton
            palette={isDirty ? undefined : 'primary'}
            template={template}
            previewTitle={title}
            label="Preview"
          />
          <Panel>
            <Tooltip content="Registration">
              <Button
                height="100%"
                leftIcon={<ChatBubbleOvalLeftEllipsisIcon boxSize={4} type="outline" />}
                onClick={() => setShowRegistration(true)}
                px={3}
                variant="ghost"
              >
                Registration
              </Button>
            </Tooltip>
          </Panel>
          <Panel>
            <Tooltip content="Manage template">
              <Button
                height="100%"
                leftIcon={<Cog6ToothIcon />}
                onClick={() => setShowSettings(true)}
                px={3}
                variant="ghost"
              >
                Manage
              </Button>
            </Tooltip>
          </Panel>
        </Flex>
        <Flex alignItems="stretch" flex={1} justifyContent="flex-end" minWidth={0}>
          {!routingEmpty && !isLayoutManageMode && (
            <Panel>
              <Tooltip content="New route">
                <Button
                  height="100%"
                  leftIcon={<PlusIcon fontSize="sm" />}
                  onClick={() => manageTraitsRef.current?.openNewRoute()}
                  palette="primary"
                  px={3}
                  variant="solid"
                >
                  New Route
                </Button>
              </Tooltip>
            </Panel>
          )}
        </Flex>
      </Flex>

      <Flex hideFrom="md" direction="column" mt="200px" alignItems="center" gap={4}>
        <Text>Open in wider view to edit</Text>
      </Flex>

      <Flex hideBelow="md" direction="column" flex={1} minHeight={0} overflow="hidden">
        {layoutManageKey == null ? (
          <ManageTemplateTraits ref={manageTraitsRef} onManageLayout={setLayoutManageKey} />
        ) : (
          <Flex flex={1} minHeight={0} overflow="hidden">
            <ManageTemplateLayout initialRootLayoutKey={layoutManageKey} />
          </Flex>
        )}
      </Flex>

      <Dialog
        bodyProps={{ overflow: 'auto' }}
        contentProps={{ maxW: '960px', width: 'min(960px, 94vw)' }}
        onClose={() => setShowRegistration(false)}
        open={showRegistration}
        title="Registration"
      >
        <Flex
          {...forceLight}
          backgroundColor="white"
          borderRadius="md"
          direction="column"
          flex="none"
          gap={2}
          overflow="auto"
          p={4}
        >
          <ManageTemplateRegistration />
        </Flex>
      </Dialog>

      <TemplateManage open={showSettings} onClose={() => setShowSettings(false)} />
    </Flex>
  )
}
